import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { useTheme } from '@material-ui/core'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import AdotelSkeleton from '../../components/AdotelSkeleton'
import apolloClient from '../../graphql/apolloClient'
import { MODIFY_RATE_PLAN_MUTATION } from '../../graphql/modifyRateplan'
import { RATEPLAN_QUERY } from '../../graphql/rateplan'
import { ROOMTYPES_QUERY } from '../../graphql/roomtype'
import { WALLET_TYPES_QUERY } from '../../graphql/walletTypes'
import { getCurrenciesAvailable } from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { Currency } from '../../types/currency'
import {
  RatePlanBasicDetailsForModify,
  RatePlanForGet,
  RatePlanForModify
} from '../../types/rateplan'
import { RoomTypeBrief } from '../../types/roomtype'
import { WalletType } from '../../types/wallet'
import { RateplanBasicDetailsForModify } from './RateplanBasicDetailsForModify'
import { RateplanFooter } from './RateplanFooter'
import { RateplanSupportedWalletTypes } from './RateplanSupportedWalletTypes'
import useStyles from './styles/RateplanMainForm.styles'
import { rateplanFormValidationSchemaForModify } from './validations/rateplanFormValidation'

const RATEPLAN_FORM_INIT_VALUES: RatePlanForGet = {
  basicDetails: {
    ratePlanCode: '',
    code: '',
    displayRatePlanCode: '',
    name: '',
    nameTranslatedList: [
      {
        label: 'Iran, Islamic Republic of',
        nativeLabel: 'فارسی',
        value: ''
      }
    ],
    roomTypeCode: '',
    boardType: '',
    ratePolicy: '',
    ratePolicyTranslatedList: [
      {
        label: 'Iran, Islamic Republic of',
        nativeLabel: 'فارسی',
        value: ''
      }
    ],
    providerCode: '',
    type: 'Physical',
    active: true,
    deleted: false,
    ratePlanID: '',
    roomTypeID: '',
    accommodationId: '',
    roomTypeName: '',
    roomTypeNameTranslatedList: [
      {
        label: 'Iran, Islamic Republic of',
        nativeLabel: 'فارسی',
        value: ''
      }
    ]
  },
  supportedWalletTypes: [
    { currency: undefined, walletTypes: [], isSelected: false }
  ]
}

const RateplanMainFormForModify = () => {
  const classes = useStyles()
  const history = useHistory()
  const theme = useTheme()
  const params = useParams<{
    accommodationID: string
    propertyCode: string
    roomTypeID: string
    roomTypeCode: string
    id: string
    ratePlanCode: string
  }>()

  const [walletTypes, setWalletTypes] = useState<
    {
      currency: Currency
      walletType: string | null
    }[]
  >([])
  const [supportedWalletTypes, setSupportedWalletTypes] = useState<
    { label: string; value: string }[]
  >([])
  const [initValues, setinitValues] = useState<RatePlanForGet>()

  const { data: roomTypes, loading: loadingForRoomTypes } = useQuery<{
    roomTypes: RoomTypeBrief[]
  }>(ROOMTYPES_QUERY, {
    variables: {
      propertyCode: params.propertyCode,
      showDeletedRoomTypes: true,
      showDeactivedRoomTypes: true
    }
  })

  const { data: walletTypeList, loading: loadingWalletTypes } = useQuery<{
    walletTypes: WalletType[]
  }>(WALLET_TYPES_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const [getRatePlan, { loading: loadingForRatePlans }] = useLazyQuery<{
    ratePlan: RatePlanForGet
  }>(RATEPLAN_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setinitValues({
        ...data.ratePlan,
        basicDetails: {
          ...data.ratePlan.basicDetails,
          code: data.ratePlan.basicDetails.displayRatePlanCode || ''
        },
        supportedWalletTypes:
          data?.ratePlan.supportedWalletTypes.map((item) => {
            return { ...item, isSelected: !!item.currency }
          }) || []
      })
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const [mutateFunction, { loading: loadingForModifyRatePlan }] = useMutation(
    MODIFY_RATE_PLAN_MUTATION
  )

  const handleSubmit = async (values: RatePlanForGet) => {
    const {
      ratePlanID,
      roomTypeID,
      accommodationId,
      roomTypeName,
      roomTypeNameTranslatedList,
      displayRatePlanCode,
      active,
      deleted,
      ratePlanCode,
      displayProviderCode,
      displayRoomTypeCode,
      roomTypeCode,
      ...remainingBasicDetails
    } = values.basicDetails

    const temp: any = []
    values.supportedWalletTypes.forEach((item) => {
      if (item.isSelected) temp.push({ ...item, isSelected: undefined })
    })
    const finalValues: RatePlanForModify = {
      basicDetails: remainingBasicDetails as RatePlanBasicDetailsForModify,
      supportedWalletTypes: temp
    }

    await mutateFunction({
      variables: {
        input: finalValues,
        propertyCode: params.propertyCode,
        roomTypeCode: values.basicDetails.roomTypeCode,
        ratePlanCode: values.basicDetails.displayRatePlanCode
      }
    })
      .then(({ data: response }) => {
        apolloClient.cache.evict({
          fieldName: 'ratePlans'
        })
        toast.success(response.modifyRatePlan.message)
        history.push(
          `/${lang}/accommodations/${params.accommodationID}/${params.propertyCode}/rate-plans/list`
        )
      })
      .catch((item) => toast.error(item.message))
  }

  useEffect(() => {
    if (getCurrenciesAvailable()) {
      setWalletTypes([
        ...getCurrenciesAvailable().map((item) => {
          return { currency: item, walletType: null }
        })
      ])
    }

    if (walletTypeList) {
      setSupportedWalletTypes([
        ...walletTypeList.walletTypes.map((item: WalletType) => {
          return { label: item.walletType, value: item.walletType }
        })
      ])
    } else {
      setSupportedWalletTypes([
        { label: getTranslate('Nothing found'), value: '' }
      ])
    }
  }, [walletTypeList, getCurrenciesAvailable().length])

  useEffect(() => {
    getRatePlan({
      variables: {
        accommodationId: params.accommodationID,
        roomTypeId: params.roomTypeID,
        ratePlanId: params.id
      }
    })
  }, [loadingForRoomTypes])

  return (
    <Formik
      enableReinitialize
      initialValues={initValues || RATEPLAN_FORM_INIT_VALUES}
      onSubmit={handleSubmit}
      validationSchema={rateplanFormValidationSchemaForModify}>
      {({
        values,
        setFieldValue,
        errors,
        touched,
        handleBlur,
        isSubmitting
      }) => {
        const isPhysical = values.basicDetails.type === 'Physical'

        return (
          <>
            <Form>
              <RateplanBasicDetailsForModify
                {...{
                  values,
                  setFieldValue,
                  errors,
                  touched,
                  handleBlur,
                  isSubmitting,
                  classes,
                  roomTypes: roomTypes ? roomTypes.roomTypes : [],
                  loadingForRoomTypes,
                  isPhysical,
                  propertyCode: params.propertyCode
                }}
              />
              <RateplanSupportedWalletTypes
                {...{
                  values,
                  setFieldValue,
                  errors,
                  touched,
                  handleBlur,
                  isSubmitting,
                  classes,
                  options: walletTypes,
                  supportedWalletTypes,
                  isPhysical
                }}
                loading={loadingWalletTypes}
              />
              <RateplanFooter
                isSubmitting={isSubmitting}
                loading={loadingForModifyRatePlan}
              />
            </Form>
            <AdotelSkeleton
              open={loadingForRatePlans}
              circlesColor={theme.palette.primary.main}
              numberOfCircles={4}
            />
          </>
        )
      }}
    </Formik>
  )
}

export default RateplanMainFormForModify
